import { get as _get } from "lodash";

import { AUDIENCE_CUTS, DIMENSIONS, FN_COLOR_OTHER, FN_PALETTE, SUPPORTED_SCHEMA_VERSION } from "./constants";

/*
  The ONLY place that reads the raw `responseData` of GET /campaign/{id}/funnelfuel-dashboard. Panels and selectors
  read the normalised model below, so a contract change touches this file and its fixtures and nothing else.

  Conventions:
  - every identifier/dimension value is a string (ids arrive as numbers, URLs and Sets want strings);
  - `leads` come back in register order (orderedLeadIds), so a filtered array is already in register order;
  - the server `panels` are kept as `baseline`: the unfiltered view and the source of fixed things (booked,
    order, target line, visibility). Panels recompute everything else from the filtered leads.
*/

export class DashboardContractError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "DashboardContractError";
    this.code = code;
  }
}

const str = (v) => ((v === null || v === undefined || v === "") ? null : String(v));
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const keyOf = (v) => str(v !== null && typeof v === "object" ? v.key : v);
// The API sends dates as dd/MM/yyyy[ HH:mm:ss] (ISO is accepted too); inside the app everything is yyyy-MM-dd.
const dateOnly = (v) => {
  const text = str(v);
  if (text === null) { return null; }

  const uk = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
  if (uk) { return `${uk[3]}-${uk[2].padStart(2, "0")}-${uk[1].padStart(2, "0")}`; }

  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(text);
  return iso ? iso[1] : null;
};

// A nullable number: "not applicable" (e.g. no rate needed with 0 days left) stays null instead of becoming 0.
const numOrNull = (v) => ((v === null || v === undefined) ? null : num(v));

// The distinct values of a lead field, most frequent first (ties A-Z).
const distinctByCount = (leads, field) => {
  const counts = {};
  leads.forEach((l) => { if (l[field] !== null) { counts[l[field]] = (counts[l[field]] || 0) + 1; } });

  return Object.keys(counts).sort((a, b) => (counts[b] - counts[a]) || a.localeCompare(b));
};
const asArray = (v) => (Array.isArray(v) ? v : []);

const normaliseSelection = (selection) => {
  const dimension = _get(selection, "dimension", null);
  const values = asArray(_get(selection, "values", [])).map(str).filter((v) => v !== null);

  return (dimension && values.length) ? { dimension, values } : null;
};

export function adaptDashboard(responseData) {
  if (responseData === null || typeof responseData !== "object") {
    throw new DashboardContractError("malformed", "Dashboard payload is missing.");
  }

  if (responseData.schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    throw new DashboardContractError("unsupported_schema", `Unsupported schemaVersion: ${responseData.schemaVersion}`);
  }

  const { campaign, capabilities, lineItemDefinitions, filterData, panels } = responseData;
  if (!campaign || !capabilities || !filterData || !panels) {
    throw new DashboardContractError("malformed", "Dashboard payload is missing campaign, capabilities, filterData or panels.");
  }

  // ---- pace: day lookup first, because leads are placed on days by activityDate ----
  const pace = _get(panels, "pace", {});
  let daily = asArray(pace.daily).map((d) => ({
    date: dateOnly(d.date),
    dayIndex: num(d.dayIndex),
    targetCumulative: num(d.targetCumulative),
    allCumulative: num(d.allCumulative),
    selectedCumulative: num(d.selectedCumulative)
  }));
  // The API may send no `daily` at all. The chart then still needs its days, so build them from the campaign
  // dates (day 0 = start date) with a straight-line target; the delivered line is counted from the leads below.
  const startDate = dateOnly(campaign.startDate);
  const totalDaysRaw = num(pace.totalDays);
  const synthesisedDays = daily.length === 0 && startDate !== null && totalDaysRaw > 0;
  if (synthesisedDays) {
    const [y, m, d] = startDate.split("-").map(Number);
    daily = Array.from({ length: totalDaysRaw }, (_, i) => ({
      date: new Date(Date.UTC(y, m - 1, d + i)).toISOString().slice(0, 10),
      dayIndex: i,
      targetCumulative: Math.round((num(pace.booked) * (i + 1)) / totalDaysRaw),
      allCumulative: 0,
      selectedCumulative: 0
    }));
  }

  const dayIndexByDate = {};
  daily.forEach((d) => { if (d.date) { dayIndexByDate[d.date] = d.dayIndex; } });

  // ---- leads, in register order ----
  const byId = {};
  asArray(filterData.leads).forEach((l) => {
    const activityDate = dateOnly(l.activityDate);

    byId[String(l.id)] = {
      id: String(l.id),
      activityDate,
      dayIndex: (activityDate !== null && dayIndexByDate[activityDate] !== undefined) ? dayIndexByDate[activityDate] : null,
      lineItemId: str(l.lineItemId),
      companyKey: str(l.companyKey),
      companyName: str(l.companyName),
      jobTitle: str(l.jobTitle),
      seniority: str(l.seniority),
      jobFunction: str(l.jobFunction),
      industry: str(l.industry),
      country: str(l.country),
      assetKey: str(l.assetKey),
      assetName: str(l.assetName)
    };
  });

  const ordered = [];
  const placed = {};
  asArray(_get(panels, "leadRegister.orderedLeadIds", [])).forEach((id) => {
    const lead = byId[String(id)];
    if (lead && !placed[lead.id]) { ordered.push(lead); placed[lead.id] = true; }
  });
  // Anything the register order does not mention still counts; it goes after the ordered ones.
  asArray(filterData.leads).forEach((l) => {
    const id = String(l.id);
    if (!placed[id]) { ordered.push(byId[id]); placed[id] = true; }
  });

  // Leads that cannot sit on the pace chart, counted from the leads themselves so the note under the chart always
  // reconciles with what is on screen (the server's own counts are shown too when they are higher).
  const campaignStart = dateOnly(campaign.startDate);
  const campaignEnd = dateOnly(campaign.endDate);
  const asOf = dateOnly(campaign.asOfDate);
  const unplotted = { missing: 0, before: 0, after: 0, future: 0 };
  ordered.forEach((l) => {
    if (l.activityDate === null) { unplotted.missing += 1; }
    else if (campaignStart && l.activityDate < campaignStart) { unplotted.before += 1; }
    else if (campaignEnd && l.activityDate > campaignEnd) { unplotted.after += 1; }
    else if (asOf && l.activityDate > asOf) { unplotted.future += 1; }
  });

  if (synthesisedDays) {
    const perDay = {};
    ordered.forEach((l) => { if (l.dayIndex !== null) { perDay[l.dayIndex] = (perDay[l.dayIndex] || 0) + 1; } });

    let run = 0;
    daily.forEach((d) => {
      run += perDay[d.dayIndex] || 0;
      d.allCumulative = run;
      d.selectedCumulative = run;
    });
  }

  // ---- lookups: names always come from the payload, a raw id is never shown ----
  const lineItemDefinitions_ = asArray(lineItemDefinitions).map((d) => ({
    id: String(d.id), name: str(d.name) || "", booked: num(d.booked)
  }));

  const lineItemName = {};
  lineItemDefinitions_.forEach((d) => { lineItemName[d.id] = d.name; });

  const companyName = {};
  const assetName = {};
  asArray(_get(panels, "companies.top", [])).forEach((c) => { if (keyOf(c.key)) { companyName[keyOf(c.key)] = str(c.label) || ""; } });
  asArray(_get(panels, "assets.rows", [])).forEach((a) => { if (keyOf(a.key)) { assetName[keyOf(a.key)] = str(a.label) || ""; } });
  ordered.forEach((l) => {
    if (l.companyKey && l.companyName) { companyName[l.companyKey] = l.companyName; }
    if (l.assetKey && l.assetName) { assetName[l.assetKey] = l.assetName; }
  });

  // ---- fixed orders ----
  // Empty orders fall back to the server's own asset rows, then to the values in the leads (most frequent first).
  let jobFunctionOrder = asArray(filterData.jobFunctionOrder).map(keyOf).filter((v) => v !== null);
  if (!jobFunctionOrder.length) { jobFunctionOrder = distinctByCount(ordered, "jobFunction"); }

  let assetOrder = asArray(filterData.assetOrder).map(keyOf).filter((v) => v !== null);
  if (!assetOrder.length) { assetOrder = asArray(_get(panels, "assets.rows", [])).map((a) => keyOf(a.key)).filter((v) => v !== null); }
  if (!assetOrder.length) { assetOrder = distinctByCount(ordered, "assetKey"); }

  // ---- job function colours: by position in the fixed order, skipping the "Other"/"Unknown" residuals (always grey) ----
  const fnColors = {};
  let paletteSlot = 0;
  jobFunctionOrder.forEach((fn) => {
    if (["other", "unknown"].includes(fn.trim().toLowerCase())) {
      fnColors[fn] = FN_COLOR_OTHER;
    } else {
      fnColors[fn] = FN_PALETTE[paletteSlot] || FN_COLOR_OTHER;
      paletteSlot += 1;
    }
  });

  // ---- values actually present per dimension, so URL-restored filters can be validated, never trusted ----
  const knownValues = {};
  DIMENSIONS.forEach((dim) => { knownValues[dim] = new Set(); });
  lineItemDefinitions_.forEach((d) => knownValues.lineItemId.add(d.id));
  assetOrder.forEach((k) => knownValues.assetKey.add(k));
  jobFunctionOrder.forEach((k) => knownValues.jobFunction.add(k));
  ordered.forEach((l) => {
    DIMENSIONS.forEach((dim) => { if (l[dim] !== null) { knownValues[dim].add(l[dim]); } });
  });

  // ---- server-provided selections, indexed so panels filter with the server's own objects ----
  const selections = {};
  const indexSelection = (raw) => {
    const s = normaliseSelection(raw);
    if (s && s.values.length === 1) { selections[`${s.dimension}|${s.values[0]}`] = s; }
    return s;
  };

  const lineItemRows = asArray(_get(panels, "lineItems.rows", []));
  lineItemRows.forEach((r) => indexSelection(r.selection));
  asArray(_get(panels, "companies.top", [])).forEach((c) => indexSelection(c.selection));
  AUDIENCE_CUTS.forEach((cut) => asArray(_get(panels, `audience.${cut}.rows`, [])).forEach((r) => indexSelection(r.selection)));
  asArray(_get(panels, "assets.rows", [])).forEach((a) => {
    indexSelection(a.selection);
    asArray(a.segments).forEach((s) => indexSelection(s.selection));
  });

  // ---- visibility: one decision per panel, from `panels.*.visible` AND `capabilities` ----
  const caps = (name) => _get(capabilities, name, false) === true;
  const audienceCuts = AUDIENCE_CUTS.filter((cut) => (
    _get(panels, "audience.visible", false) === true && _get(panels, `audience.${cut}.visible`, false) === true && caps(cut)
  ));
  const visibility = {
    pace: _get(panels, "pace.visible", false) === true && caps("pace"),
    lineItems: _get(panels, "lineItems.visible", false) === true && caps("lineItems"),
    companies: _get(panels, "companies.visible", false) === true && caps("company"),
    audience: audienceCuts.length > 0,
    assets: _get(panels, "assets.visible", false) === true && caps("asset"),
    leadRegister: _get(panels, "leadRegister.visible", false) === true
  };

  const audienceLimits = {};
  AUDIENCE_CUTS.forEach((cut) => { audienceLimits[cut] = asArray(_get(panels, `audience.${cut}.rows`, [])).length; });

  const totalDays = num(pace.totalDays);

  return {
    schemaVersion: responseData.schemaVersion,
    campaign: {
      // Null when the payload carries no id, so a missing id is never mistaken for another campaign.
      id: (campaign.id === null || campaign.id === undefined) ? null : String(campaign.id),
      name: str(campaign.name) || "",
      startDate: dateOnly(campaign.startDate),
      endDate: dateOnly(campaign.endDate),
      asOfDate: dateOnly(campaign.asOfDate),
      asOfUtc: str(campaign.asOfUtc),
      booked: num(campaign.booked),
      delivered: num(campaign.delivered),
      lineItemCount: num(campaign.lineItemCount),
      activityDateBasis: str(campaign.activityDateBasis),
      deliveredBasis: str(campaign.deliveredBasis)
    },
    capabilities: { ...capabilities },
    visibility,
    audienceCuts,
    lineItemDefinitions: lineItemDefinitions_,
    // Row order is the server's, and it never changes under a filter.
    lineItemOrder: lineItemRows.length ? lineItemRows.map((r) => String(r.id)) : lineItemDefinitions_.map((d) => d.id),
    leads: ordered,
    jobFunctionOrder,
    fnColors,
    assetOrder,
    lookups: { lineItemName, companyName, assetName },
    knownValues,
    selections,
    pace: {
      booked: num(pace.booked),
      delivered: num(pace.delivered),
      percentOfBooked: num(pace.percentOfBooked),
      plannedToDate: num(pace.plannedToDate),
      behindPlan: num(pace.behindPlan),
      currentRatePerWeek: numOrNull(pace.currentRatePerWeek),
      requiredRatePerWeek: numOrNull(pace.requiredRatePerWeek),
      totalDays,
      elapsedDays: num(pace.elapsedDays),
      daysRemaining: num(pace.daysRemaining),
      daily,
      unplottableCount: num(pace.unplottableCount),
      beforeStartCount: num(pace.beforeStartCount),
      afterEndCount: num(pace.afterEndCount),
      futureDatedCount: num(pace.futureDatedCount),
      missingDateCount: num(pace.missingDateCount),
      unplotted
    },
    // Display-only server figures that the page shows as-is or caps against.
    limits: {
      companiesTop: asArray(_get(panels, "companies.top", [])).length,
      audience: audienceLimits,
      depthBandLabels: asArray(_get(panels, "companies.depthBands", [])).map((b) => str(b.label) || "")
    },
    // The unfiltered server numbers. Used by the parity tests and as the fixed reference for the panels.
    baseline: {
      lineItems: lineItemRows.map((r) => ({
        id: String(r.id), name: str(r.name) || "", booked: num(r.booked), delivered: num(r.delivered),
        percentOfBooked: num(r.percentOfBooked), remaining: num(r.remaining)
      })),
      companies: {
        companyCount: num(_get(panels, "companies.companyCount", 0)),
        knownCompanyLeadCount: num(_get(panels, "companies.knownCompanyLeadCount", 0)),
        unknownCompanyLeadCount: num(_get(panels, "companies.unknownCompanyLeadCount", 0)),
        averageLeadsPerCompany: num(_get(panels, "companies.averageLeadsPerCompany", 0)),
        depth5Plus: num(_get(panels, "companies.depth5Plus", 0)),
        top: asArray(_get(panels, "companies.top", [])).map((c) => ({ key: keyOf(c.key), label: str(c.label) || "", count: num(c.count) })),
        depthBands: asArray(_get(panels, "companies.depthBands", [])).map((b) => ({ label: str(b.label) || "", count: num(b.count) }))
      },
      audience: AUDIENCE_CUTS.reduce((acc, cut) => {
        acc[cut] = asArray(_get(panels, `audience.${cut}.rows`, [])).map((r) => ({
          key: keyOf(r.key), label: str(r.label) || "", count: num(r.count), percentage: num(r.percentage)
        }));
        return acc;
      }, {}),
      assets: asArray(_get(panels, "assets.rows", [])).map((a) => ({
        key: keyOf(a.key), label: str(a.label) || "", count: num(a.count),
        segments: asArray(a.segments).map((s) => ({
          jobFunctionKey: keyOf(s.jobFunctionKey), label: str(s.label) || "", count: num(s.count), percentageOfAsset: num(s.percentageOfAsset)
        }))
      })),
      leadRegister: {
        total: num(_get(panels, "leadRegister.total", 0)),
        orderedLeadIds: asArray(_get(panels, "leadRegister.orderedLeadIds", [])).map(String)
      }
    }
  };
}

// The server's own selection object when it sent one, else the same shape built from the value.
export function selectionFor(model, dimension, value) {
  return model.selections[`${dimension}|${value}`] || { dimension, values: [String(value)] };
}
